import type {ServerError} from '@logistics-frontend/core/utils/network'
import type {TransitionFunction} from '@yandex-delivery/blocks'
import {externalApiURL} from '@yandex-delivery/client-core/api/corp'
import type {Optional} from '@yandex-delivery/types'
import {addQuery, delay} from '@yandex-delivery/utils'

import type {SharingRequest, SharingResponse} from 'src/shared/types'

import {appConfig} from '../../config'
import {fetch} from '../fetch'

const DELAY_TIMEOUT_MS = 1000

/**
 * Базовая функция для запроса на получение ссылки на "шарилку"
 */
function fetchSharingBase(body: SharingRequest): Promise<SharingResponse> {
  return fetch<SharingRequest, SharingResponse, SharingResponse>({
    method: 'POST',
    url: addQuery(externalApiURL('dcaa/v1/sharing'), {country: appConfig.countryCode}),
    body,
  })
}

/**
 * Запрашивает данные из шарилки.
 * @param body Тело запроса
 * @param retriesOnNetworkErrors Сколько раз ретраить в случае нетворк/500 ошибок
 */
export function fetchPollSharing(body: SharingRequest, retriesOnNetworkErrors: number = 0): Promise<SharingResponse> {
  return fetchSharingBase(body)
    .then(response => {
      return response.sharing_key
        ? response
        : delay(DELAY_TIMEOUT_MS).then(() => fetchPollSharing(body, retriesOnNetworkErrors))
    })
    .catch(error => {
      if (retriesOnNetworkErrors > 0) {
        return fetchPollSharing(body, retriesOnNetworkErrors - 1)
      }

      return Promise.reject(error)
    })
}

/**
 * Мемоизированная функция для запроса на получение ссылки на
 * "шарилку" с транзишеном для использования в useRefresh
 */
export async function fetchMemoizedPollSharing(
  body: SharingRequest,
  startTransition: TransitionFunction<Optional<VoidFunction>, ServerError>,
) {
  return pollSharing()

  async function pollSharing() {
    const response = await fetchSharingBase(body)

    if (response.sharing_key === undefined) {
      return function pollingHandler() {
        startTransition(pollSharing)
      }
    }

    window.open(response.sharing_url)
  }
}
